import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface Prescription {
  date?: string;
  daysRemaining?: number | null;
  explanation?: string;
  isHitAndRun?: boolean;
  isMinor?: boolean;
}

interface PrescriptionBadgeProps {
  prescription: Prescription | undefined | null;
  size?: 'small' | 'medium';
}

interface UrgencyStyle {
  label: string;
  bg: string;
  color: string;
  borderColor: string;
}

function buildStyle(days: number): UrgencyStyle {
  if (days < 0) {
    return {
      label: `PRESCRIBED ${Math.abs(days)}d ago`,
      bg: 'color-mix(in srgb, var(--err) 18%, transparent)',
      color: 'var(--err)',
      borderColor: 'var(--err)',
    };
  }
  if (days < 90) {
    return {
      label: `${days}d remaining — URGENT`,
      bg: 'color-mix(in srgb, var(--err) 14%, transparent)',
      color: 'var(--err)',
      borderColor: 'var(--err)',
    };
  }
  if (days < 365) {
    const months = Math.round(days / 30);
    return {
      label: `${days}d remaining (${months}m)`,
      bg: 'color-mix(in srgb, var(--warn) 14%, transparent)',
      color: 'var(--warn)',
      borderColor: 'var(--warn)',
    };
  }
  const years = Math.floor(days / 365);
  return {
    label: `${days}d remaining (${years}y)`,
    bg: 'color-mix(in srgb, var(--teal) 14%, transparent)',
    color: 'var(--teal)',
    borderColor: 'var(--teal)',
  };
}

const PrescriptionBadge: React.FC<PrescriptionBadgeProps> = ({
  prescription,
  size = 'small',
}) => {
  if (!prescription || !prescription.date) {
    return (
      <Chip
        size={size}
        label="No prescription date"
        sx={{
          bgcolor: 'var(--s3)',
          color: 'var(--t4)',
          fontSize: '0.68rem',
          height: size === 'small' ? 22 : 26,
          borderRadius: '6px',
        }}
      />
    );
  }

  const days = prescription.daysRemaining ?? 0;
  const style = buildStyle(days);
  const tooltip = [
    prescription.explanation,
    prescription.isHitAndRun ? 'Hit-and-run: 2-year prescription.' : '',
    prescription.isMinor ? 'Minor: prescription runs from 18th birthday.' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <Chip
      size={size}
      icon={
        days < 90 ? (
          <WarningAmberIcon sx={{ fontSize: 14, color: `${style.color} !important` }} />
        ) : undefined
      }
      label={style.label}
      sx={{
        bgcolor: style.bg,
        color: style.color,
        border: `1px solid ${style.borderColor}`,
        fontWeight: 600,
        fontSize: size === 'small' ? '0.68rem' : '0.76rem',
        height: size === 'small' ? 22 : 26,
        borderRadius: '6px',
        '& .MuiChip-icon': { ml: 0.5 },
      }}
    />
  );

  return tooltip ? (
    <Tooltip title={tooltip} arrow placement="top">
      <span>{content}</span>
    </Tooltip>
  ) : (
    content
  );
};

export default PrescriptionBadge;
